import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import type { BaseComponentProps } from '../../types/puck';
import { cn } from "../../lib/utils";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQAccordionProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  faqs: FAQItem[];
  layout: "single-column" | "two-column";
  backgroundColor?: string;
  /** Whether to include FAQ schema markup */
  includeSchema?: boolean;
}

const FAQAccordion: React.FC<FAQAccordionProps> = ({
  heading,
  subheading,
  faqs,
  layout = "single-column",
  backgroundColor,
  includeSchema = true,
  id,
  className,
}) => {
  // Generate FAQ schema JSON-LD
  const faqSchema =
      includeSchema && faqs?.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
              name: faq.question,
            })),
          }
        : null,

    renderAccordion = (items: FAQItem[], keyPrefix: string = "") => (
      <Accordion type="single" collapsible className="jobsite:w-full">
        {items.map((faq, idx) => (
          <AccordionItem key={`${keyPrefix}${idx}`} value={`${keyPrefix}item-${idx}`}>
            <AccordionTrigger className="jobsite:text-left jobsite:text-base jobsite:sm:text-lg jobsite:font-heading jobsite:text-foreground jobsite:py-5 jobsite:hover:text-primary jobsite:transition-colors jobsite:[&>svg]:size-5">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="jobsite:text-base jobsite:text-muted-foreground jobsite:leading-relaxed jobsite:pb-5">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    ),

    // Split FAQs for two-column layout
    midpoint = Math.ceil((faqs?.length || 0) / 2),
    leftColumn = faqs?.slice(0, midpoint) || [],
    rightColumn = faqs?.slice(midpoint) || [];

  return (
    <section
      id={id}
      className={cn(
        "jobsite:py-16 jobsite:md:py-24",
        backgroundColor ? "" : "jobsite:bg-background",
        className,
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {/* FAQ Schema JSON-LD */}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}

      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        {/* Header */}
        <div className="jobsite:text-center jobsite:mb-12 jobsite:max-w-3xl jobsite:mx-auto">
          <h2 className="jobsite:text-3xl jobsite:sm:text-4xl jobsite:mb-4 jobsite:font-heading jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground">
            {heading}
          </h2>
          {subheading && (
            <p className="jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground">{subheading}</p>
          )}
        </div>

        {/* FAQ Accordions */}
        {layout === "single-column" ? (
          <div className="jobsite:max-w-3xl jobsite:mx-auto">{renderAccordion(faqs || [])}</div>
        ) : (
          <div className="jobsite:grid jobsite:grid-cols-1 jobsite:md:grid-cols-2 jobsite:gap-8 jobsite:max-w-6xl jobsite:mx-auto">
            <div>{renderAccordion(leftColumn, "left-")}</div>
            <div>{renderAccordion(rightColumn, "right-")}</div>
          </div>
        )}
      </div>
    </section>
  );
};

export type { FAQItem, FAQAccordionProps };
export { FAQAccordion };
